import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { educationLevels } from '../data/degrees';
import { TCPAConsent } from './TCPAConsent';
const startDateOptions = ['Immediately', 'Within 1 month', 'Within 3 months', 'Within 6 months', 'Within 1 year', 'Not sure yet'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 30; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
export function HomeInlineLeadCapture() {
  const navigate = useNavigate();
  const [formStep, setFormStep] = useState(1);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    if (validateUniStep2()) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = () => {
    if (validateUniStep3()) {
      const cleanedFormData = {
        ...formData,
        phone: stripPhone(formData.phone)
      };
      console.log('Lead submitted:', cleanedFormData);
      navigate('/thank-you', {
        state: {
          formData: cleanedFormData
        }
      });
    }
  };
  const totalSteps = 3;
  const progressPercent = Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-3 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const renderFormContent = () => {
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-261" data-mp="1o59q9o9h08ex">
          <div data-id="element-262" data-mp="1o507akmuf1iq">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-263" data-mp="1o6kv7renjm7m">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-264" data-mp="1f9u0bibu6dem">
              <option value="" data-id="element-265" data-mp="15ftq521p6hz1">-- Please Select --</option>
              {educationLevels.map(level => <option key={level} value={level} data-id="element-266" data-mp="1ae4zzcrzssh9">
                  {level}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-267" data-mp="10e4blcf9nm67">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-268" data-mp="2qsejntlaht1r">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-269" data-mp="2qrv1cbrq92n7">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-270" data-mp="261ise8et0ia8">
              <option value="" data-id="element-271" data-mp="1w31zv707vvm2">-- Please Select --</option>
              {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-272" data-mp="1r59w9fq7ml21">
                  {opt}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-273" data-mp="1n3ychqu0ou1r">{errors.startDate}</p>}
          </div>

          <div data-id="element-274" data-mp="1ibohvxhyj5t5">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-275" data-mp="1ib5ahapxnztd">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-276" data-mp="18flfqrn3v767">
              <option value="" data-id="element-277" data-mp="zdu8hicx2sa8">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-278" data-mp="3b0xtv1wrk15q">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-279" data-mp="3f1hr05hc74rv">{errors.gradYear}</p>}
          </div>

          <div className="pt-2" data-id="element-280" data-mp="3k1ch46tafqbv">
            <button type="button" onClick={handleStep1Next} className="w-full hover:bg-[#047857] text-white font-bold py-4 px-6 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl bg-[#D4A943]" data-id="element-281" data-mp="3k25foki3f9ix">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-282" data-mp="31be7vndr7r6n">
          <div data-id="element-283" data-mp="31acp0wqhy0ed">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-284" data-mp="319thm9yh2uel">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-285" data-mp="28n8op52cma8x" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-286" data-mp="2fax8j9r6ncv6">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-287" data-mp="2ab2hn67eqna4">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-288" data-mp="1mm01k2sf92hq" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-289" data-mp="1imhhtacc6xgh">
                <div data-id="element-290" data-mp="1wl7dqmnf6r39">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-291" data-mp="1wlqva295rdgr">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="City" data-id="element-292" data-mp="37yjlh8mavtup" />
                </div>
                <div data-id="element-293" data-mp="trtqwmb6izm0">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-294" data-mp="tt4wmro6qhfr">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy bg-gray-50" placeholder="State" data-id="element-295" data-mp="kq3s2veywye7" />
                </div>
              </div>
              <div data-id="element-296" data-mp="ypvbxcs61904">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-297" data-mp="yqekw3zu8n20">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal normal-case" data-id="element-298" data-mp="1reaoztrqlh8t">
                    (optional)
                  </span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-299" data-mp="1w7vfz1uwxayi" />
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-300" data-mp="3dkkhipenvv41">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-301" data-mp="3dllr0pg149jn">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-302" data-mp="a1r6okd9pkdk" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-[#059668] hover:bg-[#047857] text-white font-bold py-4 px-6 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl" data-id="element-303" data-mp="56k5pkbdutsc">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-304" data-mp="11c3sae2f57v0">
          <div className="grid grid-cols-2 gap-3" data-id="element-305" data-mp="11cwzth97c1e7">
            <div data-id="element-306" data-mp="15bw4r1uxny5n">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-307" data-mp="15dgzosl5tj41">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-308" data-mp="1f30jc0gp5gzy" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-309" data-mp="1ozlr2tk3p67h">{errors.firstName}</p>}
            </div>
            <div data-id="element-310" data-mp="1t3ba73i0avl4">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-311" data-mp="1t2rsnnw9q97m">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-312" data-mp="3uuenk0ja6o9d" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-313" data-mp="283iijbap46dr">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-314" data-mp="2d228rnwbo4bx">
            <div data-id="element-315" data-mp="2i0wpmkaqbite">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-316" data-mp="2i0dhfvavs8sk">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-317" data-mp="2r1baxjnp0xww" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-318" data-mp="30ql4l3y6qon4">{errors.email}</p>}
            </div>
            <div data-id="element-319" data-mp="35py3in2bzm62">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-320" data-mp="35qh8l36vug2o">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-321" data-mp="3eqvxecj22dax" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-322" data-mp="z0w2rsvk6ko5">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2" data-id="element-323" data-mp="u3xcjlf0mxv8">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-324" data-mp="u4yods3uvoe0">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-navy focus:ring-navy cursor-pointer flex-shrink-0" data-id="element-325" data-mp="p5c819sqds0z" />
              <span className="text-xs text-gray-600 leading-relaxed" data-id="element-326" data-mp="1d1qbydqfkpnv">
                By submitting this form, you are giving your express written
                consent to be contacted regarding our services using email,
                telephone or text, including our use of automated technology for
                calls or texts to the number provided above.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-327" data-mp="3uywnkcyvojn9">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-328" data-mp="2vvodo4dfgmb">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-329" data-mp="2ukmjtuhkixu">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-330" data-mp="3bb5pou0fsht1" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} className="flex-1 bg-[#A52A2A] hover:bg-[#8B1A1A] text-white font-bold py-4 px-6 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl" data-id="element-331" data-mp="3ga8t6a0rqfyw">
              Get My Results
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 sticky top-6 ring-1 ring-gray-900/5" data-id="element-332" data-mp="10kfibw8rillr">
      <div className="px-6 py-4 bg-[#1B365D]" data-id="element-333" data-mp="15g4le9jzqkmt">
        <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-334" data-mp="1flcq7uwkb17x">
          Complete Your Profile
        </h2>
        <p className="text-gray-300 text-xs text-center mt-1" data-id="element-335" data-mp="1aovy6pm8s2pk">
          Get matched with the best programs
        </p>
      </div>

      <div className="px-6 pt-4" data-id="element-336" data-mp="1jnr37ydjv6ra">
        <div className="flex justify-between items-center mb-1.5" data-id="element-337" data-mp="1jmof1y7m3if2">
          <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-338" data-mp="1ycdjq3ewy4k3">
            Step {formStep} of {totalSteps}
          </span>
          <span className="text-xs text-gray-500" data-id="element-339" data-mp="1td0yto7lr7k5">{progressPercent}%</span>
        </div>
        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-340" data-mp="27c0n2ur7dkfs">
          <motion.div className="h-full rounded-full bg-[#A52A2A]" initial={{
          width: 0
        }} animate={{
          width: `${progressPercent}%`
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} data-id="element-341" data-mp="23bgqptegekup" />
        </div>
      </div>

      <div className="p-6" data-id="element-342" data-mp="aeuz8hr6edm5">
        <AnimatePresence mode="wait" data-id="element-343" data-mp="afnwms48vqrm">
          <motion.div key={formStep} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2
        }} data-id="element-344" data-mp="ael6wninruda">
            {renderFormContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}